import { analyticsConsentGranted, measurementId } from "./cookie_consent"

const ENHANCED_MEASUREMENT_EXTENSIONS = new Set(["7z", "csv", "doc", "docx", "exe", "gz", "mp3", "mp4", "msi", "pdf", "ppt", "pptx", "rar", "rtf", "tar", "txt", "wav", "wma", "wmv", "xls", "xlsx", "xml", "zip"])
const IGNORED_SEARCH_FIELDS = /^(authenticity_token|commit|page|per|sort|order|utf8|locale)$/i
const basename = (value) => (value || "").split(/[\\/]/).pop()
const extension = (value) => basename(value).match(/\.([a-z0-9]+)$/i)?.[1].toLowerCase() || ""

function typeExtension(link) {
  const mime = (link.getAttribute("type") || "").toLowerCase()
  if (mime.includes("pdf")) return "pdf"
  if (mime.includes("excel") || mime.includes("spreadsheet")) return "xls"
  if (mime.includes("zip")) return "zip"
  if (mime.includes("png")) return "png"
  return ""
}

function inferredCategory(link, url) {
  if (link.dataset.downloadCategory) return link.dataset.downloadCategory
  if (/\/(members|garages|attendees|dictionary|emg_customers).*download|\/dictionary\/search/i.test(url.pathname)) return "export"
  if (/\/emg\//i.test(url.pathname)) return "emg_document"
  if (/\/din\//i.test(url.pathname)) return "din_document"
  return "alchemy_attachment"
}

export function downloadEventParameters(link) {
  if (!(link instanceof HTMLAnchorElement) || !link.hasAttribute("download")) return null
  const url = new URL(link.href, window.location.href)
  const urlExtension = extension(url.pathname)
  if (ENHANCED_MEASUREMENT_EXTENSIONS.has(urlExtension)) return null
  const fileName = basename(link.getAttribute("download")) || basename(url.pathname)
  const emgLanguage = (link.dataset.emgLanguage || "").toLowerCase()
  return { file_name: fileName, file_extension: extension(fileName) || typeExtension(link) || urlExtension, link_url: `${url.origin}${url.pathname}`, download_category: inferredCategory(link, url), ...(emgLanguage && { emg_language: emgLanguage }), page_path: window.location.pathname }
}

export function emgHtmlOpenEventParameters(link) {
  if (!(link instanceof HTMLAnchorElement) || !link.hasAttribute("data-analytics-emg-html") || !link.dataset.downloadCategory) return null
  const url = new URL(link.href, window.location.href)
  return { file_name: "VPI-European Maintenance Guide", file_extension: "html", link_url: `${url.origin}${url.pathname}`, download_category: link.dataset.downloadCategory, emg_language: (link.dataset.emgLanguage || "").toLowerCase(), page_path: window.location.pathname }
}

export function searchEventParameters(form) {
  if (!(form instanceof HTMLFormElement) || !form.dataset.analyticsSearchArea) return null
  const entries = [...new FormData(form).entries()].filter(([name, value]) => !IGNORED_SEARCH_FIELDS.test(name) && String(value).trim() !== "")
  const hasQuery = entries.some(([name]) => {
    const field = form.elements.namedItem(name)
    return field?.type === "search" || name === "q" || /(_cont|search)/i.test(name)
  })
  const uniqueCriteria = new Set(entries.map(([name]) => name)).size
  const result = { search_area: form.dataset.analyticsSearchArea, has_query: hasQuery, active_filter_count: uniqueCriteria, page_path: window.location.pathname }
  return result
}

function trackEvent(name, parameters) {
  const destination = measurementId()
  if (!destination) return

  window.gtag("event", name, { ...parameters, send_to: destination })
}

export function initAnalyticsEvents({ consentGranted = analyticsConsentGranted } = {}) {
  window.__vpiAnalyticsConsentGranted = consentGranted
  if (window.__vpiAnalyticsEventsInitialized) return
  window.__vpiAnalyticsEventsInitialized = true
  document.addEventListener("click", (event) => {
    const link = event.target.closest?.("a[download]")
    const parameters = link && downloadEventParameters(link)
    if (parameters && window.__vpiAnalyticsConsentGranted() && typeof window.gtag === "function") trackEvent("file_download", parameters)

    const emgHtmlLink = event.target.closest?.("a[data-analytics-emg-html]")
    const emgHtmlParameters = emgHtmlLink && emgHtmlOpenEventParameters(emgHtmlLink)
    if (emgHtmlParameters && window.__vpiAnalyticsConsentGranted() && typeof window.gtag === "function") trackEvent("file_download", emgHtmlParameters)
  })
  document.addEventListener("submit", (event) => {
    if (event.target.dataset.analyticsSearchMode === "live") return
    const parameters = searchEventParameters(event.target)
    if (parameters && window.__vpiAnalyticsConsentGranted() && typeof window.gtag === "function") trackEvent("search", parameters)
  })
  document.addEventListener("analytics:search", (event) => {
    const parameters = searchEventParameters(event.target)
    if (parameters && window.__vpiAnalyticsConsentGranted() && typeof window.gtag === "function") trackEvent("search", parameters)
  })
}
